import { createResourceConfig, buildInfo } from '@cbsinteractive/avia-js';
import { v4 as uuidv4 } from 'uuid';
import * as queryString from 'querystring';
import config from 'config';
import device, { isUnknownOrNull } from 'common/support/device';
import { VideoLike as Video } from 'store-types';

import { shouldUseDASH } from 'support/videoHelper';
import { getFMSFallbackParams } from 'support/fmsHelper';
import { ResourceConfig } from 'store-types';

export type { ResourceConfig };

const BUILD_PLATFORM = config.buildPlatform;

const getLATString = (isLAT: boolean | number | null): string => {
  let latFromCmp: number | undefined = 0;
  if (
    window.cmp &&
    window.cmp.advertisements.ready &&
    window.cmp.advertisements.getLat() !== undefined
  ) {
    latFromCmp = window.cmp.advertisements.getLat();
  }
  return isLAT === true || isLAT === 1 || Number(latFromCmp) ? '1' : '0';
};

export const getGAMAdTrackingUrlParams = async (
  externalRDID: string | null | undefined = null,
  externalIsLAT: boolean | number | null | undefined = null,
): Promise<Record<string, string>> => {
  const rdid = externalRDID || (await device.getRDID());
  const isLAT = externalIsLAT || (await device.getLAT());

  let result: Record<string, string> = {
    idtype: 'dpid',
  };

  if (!isUnknownOrNull(rdid) && !isUnknownOrNull(isLAT)) {
    result = {
      ...result,
      rdid,
      is_lat: getLATString(isLAT),
    };
  }

  return result;
};

export const getFWAdTrackingUrlParams = async (
  externalRDID: string | null | undefined = null,
  externalIsLAT: boolean | number | null | undefined = null,
): Promise<Record<string, string | number | undefined>> => {
  const privacyParams: {
    tcf?: string;
    usp?: string;
    gpp?: string;
    gppsid: string;
    rdp?: string;
  } = {
    ...window.cmp.advertisements.getState(),
    ...{ gppsid: window.cmp.advertisements.getGppSID() },
  };
  const rdid = externalRDID || (await device.getRDID());
  const isLAT = externalIsLAT || (await device.getLAT());
  const gdprApplies = window.cmp.consent.getConsentModel() === 'gdpr';

  let result: Record<string, string | number | undefined> = {};

  if (device.usePPID) {
    result = {
      ...result,
      imafw_ppid: rdid,
    };
  } else if (!isUnknownOrNull(isLAT)) {
    result = {
      ...result,
      imafw__fw_is_lat: getLATString(isLAT),
      _fw_is_lat: getLATString(isLAT),
    };
  }

  if (!isUnknownOrNull(rdid)) {
    const fwDid =
      getLATString(isLAT) === '1'
        ? `${getPlatformDeviceIdPrefix()}00000000-0000-0000-0000-000000000000`
        : `${getPlatformDeviceIdPrefix()}${rdid}`;

    result = {
      ...result,
      imafw__fw_did: fwDid,
    };
  }

  result = {
    ...result,
    _fw_gdpr: gdprApplies ? 1 : 0,
    imafw__fw_gdpr: gdprApplies ? 1 : 0,
    _fw_gdpr_consent: privacyParams.tcf,
    imafw__fw_gdpr_consent: privacyParams.tcf,
    _fw_us_privacy: privacyParams.usp,
    imafw__fw_us_privacy: privacyParams.usp,
    us_privacy: privacyParams.usp,
    gpp: privacyParams.gpp,
    imafw_gpp: privacyParams.gpp,
    gpp_sid: privacyParams.gppsid,
    imafw_gpp_sid: privacyParams.gppsid,
    rdp: privacyParams.rdp,
  };

  return result;
};

const sessions = ['a', 'b', 'c', 'd', 'e', 'f'];
const subSessions = ['1', '2', '3', '4'];

const getRandomElement = (array: string[]): string => {
  if (!array || array.length === 0) return '';
  return array[Math.floor(Math.random() * array.length)];
};

const getGAMCustomParams = async (useSessions: boolean = false): Promise<string> => {
  const result: Record<string, string> = {
    vguid: uuidv4(),
  };

  const rdid = await device.getRDID();

  if (!isUnknownOrNull(rdid)) result.ifa = rdid;

  if (useSessions) {
    result.session = getRandomElement(sessions);
    result.subses = getRandomElement(subSessions);
  }

  return queryString.stringify(result);
};

const getFWCustomParams = async (useSessions: boolean = false): Promise<Record<string, string>> => {
  let result: Record<string, string> = {};

  if (useSessions) {
    result = {
      ...result,
      imafw_session: getRandomElement(sessions),
      imafw_subses: getRandomElement(subSessions),
      imafw_vguid: uuidv4(),
    };
  }

  return result;
};

const constructGAMDAIUrl = async (video: Video): Promise<string> => {
  const { daiIu, daiOt, daiOv, daiAssetKey, daiApto } = video?.ads ?? {};

  const adTrackingParams = await getGAMAdTrackingUrlParams();
  const custParams = await getGAMCustomParams(true);
  const urlParams: Record<string, string | number> = {
    description_url: config.playback.ima.descriptionUrl,
    an: config.playback.ima.appName,
    url: config.playback.ima.url,
    msid: config.playback.ima.appId,
    cust_params: custParams,
    'dai-apto': daiApto || 2000,
    ...adTrackingParams,
  };

  if (daiIu) urlParams.iu = daiIu;
  if (daiOt) urlParams['dai-ot'] = daiOt;
  if (daiOv) urlParams['dai-ov'] = daiOv;

  const query = queryString.stringify(urlParams);
  const streamType = shouldUseDASH(video) ? 'dash' : 'hls';
  const extension = streamType === 'dash' ? 'manifest.mpd' : 'master.m3u8';
  return `https://dai.google.com/linear/${streamType}/event/${daiAssetKey}/${extension}?${query}`;
};

const constructFWDAIUrl = async (
  video: Video,
  fmsParams: Record<string, string | null> | null,
  csid: string | null,
): Promise<string> => {
  const { daiAssetKey, daiApto, daiCsid } = video?.ads ?? {};
  const adTrackingParams = await getFWAdTrackingUrlParams();
  const custParams = await getFWCustomParams(true);

  const csidTemplate = csid || config.playback.ima.csid;
  const channelId = daiCsid ? `_${daiCsid}` : '';

  const urlParams = {
    imafw_csid: `${csidTemplate + channelId}`,
    imafw__fw_h_referer: config.playback.ima.bundle,
    imafw_live_asset_id: daiAssetKey,
    imafw_description_url: config.playback.ima.descriptionUrl,
    imafw_playername_version: `${buildInfo.playerName.replace(/ /g, '_')}_${
      buildInfo.playerVersion
    }`,
    ...adTrackingParams,
    ...custParams,
    ...(fmsParams || (await getFMSFallbackParams())),
    imafw_sz: '640x480',
    imafw__fw_coppa: '0',
    imafw_tfcd: '0',
    'dai-apto': daiApto || 2000,
  };

  const query = queryString.stringify(urlParams);
  const streamType = shouldUseDASH(video) ? 'dash' : 'hls';
  const extension = streamType === 'dash' ? 'manifest.mpd' : 'master.m3u8';
  return `https://dai.google.com/linear/${streamType}/event/${daiAssetKey}/${extension}?${query}`;
};

const constructDAIResource = async (
  video: Video,
  adSource: string,
  fmsParams: Record<string, string | null> | null | undefined = null,
  csid: string | null | undefined = null,
  id3Owners: string[] | null | undefined = null,
): Promise<ResourceConfig> => {
  const res = createResourceConfig() as ResourceConfig;
  switch (adSource) {
    case 'fw':
      res.location.mediaUrl = await constructFWDAIUrl(video, fmsParams, csid);
      break;
    case 'gam':
    default:
      res.location.mediaUrl = await constructGAMDAIUrl(video);
      break;
  }

  res.playback.id3OwnerIds = id3Owners;
  return res as ResourceConfig;
};

export const createDAIAPIContentResource = async (
  video: Video,
  csid: string,
  fmsParams: Record<string, string | null> | null = null,
  csidVod?: string,
): Promise<ResourceConfig> => {
  const res = createResourceConfig() as ResourceConfig;
  const { daiAssetKey, daiApto } = video?.ads ?? {};
  const adTrackingParams = await getFWAdTrackingUrlParams();
  const custParams = await getFWCustomParams(true);

  const urlParams = {
    imafw_csid: csidVod || config.playback.ima.csid,
    imafw__fw_h_referer: config.playback.ima.bundle,
    imafw_live_asset_id: daiAssetKey,
    imafw_description_url: config.playback.ima.descriptionUrl,
    imafw_playername_version: `${buildInfo.playerName.replace(/ /g, '_')}_${
      buildInfo.playerVersion
    }`,
    ...adTrackingParams,
    ...custParams,
    ...(fmsParams || (await getFMSFallbackParams())),
    imafw_sz: '640x480',
    imafw__fw_coppa: '0',
    imafw_tfcd: '0',
    'dai-apto': daiApto || 2000,
  };

  res.ad.ssai = {
    contentSourceId: csid,
    videoId: video.trackingId,
    isLive: false,
    useApi: true,
    adParameters: urlParams,
  };
  res.location.mediaUrl = video?.playback?.url;

  return res;
};

export const constructGAMDAIResource = async (video: Video): Promise<ResourceConfig> => {
  return constructDAIResource(video, 'gam');
};

export const constructFWDAIResource = async (
  video: Video,
  fmsParams: Record<string, string | null> | null = null,
  csid: string | null = null,
  id3Params: string[] | null | undefined = null,
): Promise<ResourceConfig> => {
  return constructDAIResource(video, 'fw', fmsParams, csid, id3Params);
};

const constructResourceFromUrl = (
  streamUrl?: string,
  id3Owners: string[] | null | undefined = null,
): ResourceConfig => {
  const res = createResourceConfig() as ResourceConfig;
  res.location.mediaUrl = streamUrl;
  res.playback.id3OwnerIds = id3Owners;

  return res as ResourceConfig;
};

export const appendHLSEncryptionConfig = (
  resource?: ResourceConfig,
  token?: string,
): ResourceConfig => {
  const res = resource || createResourceConfig() as ResourceConfig;

  res.location.drm.enabled = true;
  res.location.drm.aes.header = {
    authorization: `Bearer ${token}`,
  };
  res.location.drm.aes.provider = config.playback.drm.aes.provider;

  return res as ResourceConfig;
};

export const createNonDAIContentResource = (
  video: Video,
  id3Params: string[] | null = null,
): ResourceConfig => {
  return constructResourceFromUrl(video?.playback?.url, id3Params);
};

export const createSyncbakResource = (
  streamUrl: string,
  id3Params: string[] | null = null,
): ResourceConfig => {
  return constructResourceFromUrl(streamUrl, id3Params);
};

export const appendFullDRMConfig = (
  resource?: ResourceConfig,
  token: string | null | undefined = null,
  contentId: string | null | undefined = null,
  widevineUrl?: string,
  playreadyUrl?: string,
): ResourceConfig => {
  const res = resource || createResourceConfig() as ResourceConfig;

  res.location.drm.enabled = true;

  res.location.drm.widevine = {
    url: replaceUrlPlaceholders(widevineUrl, contentId, token),
    header: {
      authorization: `Bearer ${token}`,
    },
  };
  res.location.drm.playready = {
    url: replaceUrlPlaceholders(playreadyUrl, contentId, token),
    header: {
      authorization: `Bearer ${token}`,
    },
  };

  return res as ResourceConfig;
};

const replaceUrlPlaceholders = (
  url?: string,
  contentId: string | null | undefined = null,
  token: string | null | undefined = null,
  keyId: string | null | undefined = null,
): string => {
  return (url ?? '')
    .replace('{content_ID}', (contentId ?? undefined) as string)
    .replace('{JWT-Token}', (token ?? undefined) as string)
    .replace('{key-ID}', (keyId ?? undefined) as string);
};

const getPlatformDeviceIdPrefix = (): string => {
  switch (BUILD_PLATFORM) {
    case 'vizio':
      return 'vida:';
    case 'samsung-tizen':
      return 'tifa:';
    case 'lg-webos':
      return 'lgudid:';
    default:
      return '';
  }
};
